/**
 * Find a customer, then pick one of their sites. Shared by New job and New
 * quote — both need exactly this, and both need it to say what to do when the
 * customer isn't there yet or has nowhere for the work to happen.
 */
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { api } from "../lib/api";
import type { CustomerDetail, SiteRow } from "../lib/types";

export function CustomerSitePicker({
  customerId,
  siteId,
  onCustomer,
  onSite,
}: {
  customerId: string;
  siteId: string;
  onCustomer: (id: string) => void;
  onSite: (id: string) => void;
}) {
  const [search, setSearch] = useState("");
  const found = useQuery({
    queryKey: ["customers", search.trim()],
    queryFn: () => api.customers.list(search.trim() || undefined),
    enabled: !customerId,
    placeholderData: (p) => p,
  });
  const customer = useQuery({
    queryKey: ["customer", customerId],
    queryFn: () => api.customers.get(customerId),
    enabled: Boolean(customerId),
  });

  // One site: pick it. A site that isn't this customer's: forget it.
  useEffect(() => {
    const sites = customer.data?.sites ?? [];
    if (!customer.data) return;
    if (sites.length === 1 && siteId !== sites[0]!.id && !sites.some((s) => s.id === siteId)) onSite(sites[0]!.id);
    else if (siteId && !sites.some((s) => s.id === siteId)) onSite("");
  }, [customer.data, siteId, onSite]);

  if (!customerId) {
    return (
      <>
        <label className="field">
          <span>Find the customer</span>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, suburb, street, contact" autoFocus />
        </label>
        <div className="stack" style={{ marginTop: 8, maxHeight: 280, overflowY: "auto" }}>
          {(found.data ?? []).slice(0, 12).map((c) => (
            <button
              key={c.id}
              type="button"
              className="btn"
              style={{ justifyContent: "flex-start", textAlign: "left" }}
              onClick={() => onCustomer(c.id)}
            >
              <b>{c.name}</b>
              <span className="tiny muted" style={{ marginLeft: 8 }}>
                {c.site_count} site{c.site_count === 1 ? "" : "s"}
                {c.primary_contact ? ` · ${c.primary_contact}` : ""}
              </span>
            </button>
          ))}
          {found.data && found.data.length === 0 && (
            <p className="tiny muted">
              Nobody matches. <Link to="/customers">Add them on Customers</Link> first.
            </p>
          )}
        </div>
      </>
    );
  }

  const c: CustomerDetail | undefined = customer.data;
  const site: SiteRow | undefined = c?.sites.find((s) => s.id === siteId);
  return (
    <>
      <div className="doc-row">
        <b>{c?.name ?? "…"}</b>
        <span className="doc-actions">
          <button
            className="linkish tiny"
            type="button"
            onClick={() => {
              onCustomer("");
              onSite("");
            }}
          >
            Change
          </button>
        </span>
      </div>
      {c?.on_credit_hold && <p className="notice notice-warn">This customer is on credit hold.</p>}
      {c && c.sites.length === 0 ? (
        <p className="notice notice-warn">
          They have no site yet. <Link to={`/customers/${customerId}`}>Add one</Link> — the work needs somewhere to
          happen.
        </p>
      ) : (
        <label className="field" style={{ marginTop: 8 }}>
          <span>Site</span>
          <select value={siteId} onChange={(e) => onSite(e.target.value)}>
            <option value="">Choose…</option>
            {(c?.sites ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
                {s.suburb ? ` — ${s.suburb}` : ""}
              </option>
            ))}
          </select>
        </label>
      )}
      {site?.access_notes && (
        <p className="tiny" style={{ whiteSpace: "pre-line" }}>
          {site.access_notes}
        </p>
      )}
    </>
  );
}
